import React from "react";
import { Controller } from "react-hook-form";
import { NewInput, TextBox, ImageBox } from "../../../components/inputs/main";
import { IoIosAdd } from "react-icons/io";
import { MdDelete } from "react-icons/md";

interface FormData {
  toolName: string;
  description: string;
  contactEmailAddress: string;
  website: string;
  webinar: string;
  logo: string;
  additionalInformation: string;
  toolCreatedBy: string;
  toolOwnerAddress: string;
  organizationName: string;
}

interface ResourceItem {
  material?: string;
  link?: string;
  logo?: string | File;
}

interface UtilityItem {
  address?: string;
}

interface GeneralInformationStepProps {
  control: any;
  errors: any;
  getSearch: URLSearchParams;
  itemForm: ResourceItem[];
  itemUtil: UtilityItem[];
  logo: File | null;
  setLogo: (file: File | null) => void;
  handleInputChangeForMutipleItem: (
    event: React.ChangeEvent<HTMLInputElement> | any,
    index: number,
    field: string
  ) => void;
  handleInputChangeForMutipleItemUtil: (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
    field: string
  ) => void;
  handleDeleteRowForMutipleItem: (index: number) => void;
  handleDeleteRowForMutipleItemUtil: (index: number) => void;
  addRowForMutipleItem: () => void;
  addRowForMutipleItemUtils: () => void;
}

const GeneralInformationStep: React.FC<GeneralInformationStepProps> = ({
  control,
  errors,
  getSearch,
  itemForm,
  itemUtil,
  logo,
  setLogo,
  handleInputChangeForMutipleItem,
  handleInputChangeForMutipleItemUtil,
  handleDeleteRowForMutipleItem,
  handleDeleteRowForMutipleItemUtil,
  addRowForMutipleItem,
  addRowForMutipleItemUtils,
}) => {
  const isAuthenticated =
    getSearch.get("contactEmailAddress") && getSearch.get("requestId");

  return (
    <div className="mt-12 bg-white w-full p-8">
      <div
        style={{ borderBottom: "1px solid #E2E8F0" }}
        className="h-full lg:w-2/3 pb-3"
      >
        <h4 className="text-xl font-semibold inter text-da-blue-600">
          General Information
        </h4>
      </div>
      <div className="mt-6">
        <div className="lg:w-2/3 space-y-5">
          {!isAuthenticated && (
            <div>
              <Controller
                name="organizationName"
                control={control}
                rules={{
                  required: !isAuthenticated ? "This field is required" : null,
                }}
                render={({ field: { value, onChange, name } }) => (
                  <NewInput
                    type="text"
                    required
                    name={name}
                    value={value}
                    label="Organization Name"
                    onChange={onChange}
                    placeholder="Esawas"
                  />
                )}
              />
              {errors.organizationName && (
                <p className="text-[#dc2626] text-xs">
                  {errors.organizationName.message}
                </p>
              )}
            </div>
          )}

          {/* Tool Name and Website in 2-column grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-4">
            <div>
              <Controller
                name="toolName"
                control={control}
                rules={{ required: "This field is required" }}
                render={({ field: { value, onChange, name } }) => (
                  <NewInput
                    type="text"
                    required
                    name={name}
                    value={value}
                    label="Tool Name"
                    onChange={onChange}
                    placeholder="Type your product name"
                  />
                )}
              />
              {errors.toolName && (
                <p className="text-[#dc2626] text-xs">
                  {errors.toolName.message}
                </p>
              )}
            </div>
            <div>
              <Controller
                name="website"
                control={control}
                rules={{
                  required: "This field is required",
                  pattern: {
                    value:
                      /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi,
                    message: "Invalid url format",
                  },
                }}
                render={({ field: { value, onChange, name } }) => (
                  <NewInput
                    type="url"
                    name={name}
                    value={value}
                    label="Tool Website (or developer's organisation website if tool website unavailable)"
                    onChange={onChange}
                    required
                    placeholder="Tool Owner's Website"
                  />
                )}
              />
              {errors.website && (
                <p className="text-[#dc2626] text-xs">
                  {errors.website.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <Controller
              name="description"
              control={control}
              rules={{ required: "This field is required" }}
              render={({ field: { value, onChange, name } }) => (
                <TextBox
                  name={name}
                  value={value}
                  onChange3={onChange}
                  label="Tool Description"
                  setState={(e: any[]) => onChange(e)}
                  required
                  type="editor"
                  placeholder="Describe the tool, company, tool's purpose, key features, and how it benefits users or organizations."
                />
              )}
            />
            {errors.description && (
              <p className="text-[#dc2626] text-xs">
                {errors.description.message}
              </p>
            )}
          </div>

          <div>
            <Controller
              name="contactEmailAddress"
              control={control}
              rules={{
                required: "This field is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Invalid email format",
                },
              }}
              render={({ field: { value, onChange, name } }) => (
                <NewInput
                  required
                  type="email"
                  name={name}
                  value={value}
                  label="Contact Email Address"
                  placeholder="email address"
                  onChange={onChange}
                />
              )}
            />
            {errors.contactEmailAddress && (
              <p className="text-[#dc2626] text-xs">
                {errors.contactEmailAddress.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-4">
            <div>
              <Controller
                name="toolCreatedBy"
                control={control}
                rules={{ required: "This field is required" }}
                render={({ field: { value, onChange, name } }) => (
                  <NewInput
                    type="text"
                    name={name}
                    value={value}
                    label="Tool Developed By"
                    onChange={onChange}
                    required
                    placeholder="Tool Developed By"
                  />
                )}
              />
              {errors.toolCreatedBy && (
                <p className="text-[#dc2626] text-xs">
                  {errors.toolCreatedBy.message}
                </p>
              )}
            </div>
            <div>
              <Controller
                name="toolOwnerAddress"
                control={control}
                rules={{ required: "This field is required" }}
                render={({ field: { value, onChange, name } }) => (
                  <NewInput
                    type="text"
                    name={name}
                    value={value}
                    label="Tool Owner Address"
                    placeholder="Enter Full Address"
                    onChange={onChange}
                  />
                )}
              />
              {errors.toolOwnerAddress && (
                <p className="text-[#dc2626] text-xs">
                  {errors.toolOwnerAddress.message}
                </p>
              )}
            </div>
          </div>

          <div className="mb-4">
            <Controller
              name="logo"
              control={control}
              rules={{ required: !logo ? "This field is required" : false }}
              render={({ field: { value, onChange, name } }) => (
                <ImageBox
                  type="url"
                  name={name}
                  value={value}
                  label="Logo"
                  placeholder="Type your logo url"
                  onChange={onChange}
                  setState={(e: any) => setLogo(e)}
                  data={value}
                  logo={logo}
                />
              )}
            />
            {errors.logo && (
              <p className="text-[#dc2626] text-xs">{errors.logo.message}</p>
            )}
          </div>

          <ResourcesSection
            control={control}
            errors={errors}
            itemForm={itemForm}
            handleInputChangeForMutipleItem={handleInputChangeForMutipleItem}
            handleDeleteRowForMutipleItem={handleDeleteRowForMutipleItem}
            addRowForMutipleItem={addRowForMutipleItem}
          />

          <UtilitiesSection
            itemUtil={itemUtil}
            handleInputChangeForMutipleItemUtil={
              handleInputChangeForMutipleItemUtil
            }
            handleDeleteRowForMutipleItemUtil={
              handleDeleteRowForMutipleItemUtil
            }
            addRowForMutipleItemUtils={addRowForMutipleItemUtils}
          />

          <div>
            <Controller
              name="additionalInformation"
              control={control}
              render={({ field: { value, onChange, name } }) => (
                <TextBox
                  name={name}
                  value={value}
                  onChange3={onChange}
                  label="Additional Information"
                  placeholder="Any other information"
                  setState={(e: any[]) => onChange(e)}
                  type="editor"
                />
              )}
            />
            {errors.additionalInformation && (
              <p className="text-[#dc2626] text-xs">
                {errors.additionalInformation.message}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Resources Section Component
const ResourcesSection: React.FC<{
  control: any;
  errors: any;
  itemForm: ResourceItem[];
  handleInputChangeForMutipleItem: (
    event: React.ChangeEvent<HTMLInputElement> | any,
    index: number,
    field: string
  ) => void;
  handleDeleteRowForMutipleItem: (index: number) => void;
  addRowForMutipleItem: () => void;
}> = ({
  control,
  errors,
  itemForm,
  handleInputChangeForMutipleItem,
  handleDeleteRowForMutipleItem,
  addRowForMutipleItem,
}) => {
  return (
    <div className="pt-5">
      <label className="text-[#334155] font-medium text-sm inter mt-4">
        Resources
      </label>
      <div className="py-4">
        <Controller
          name="webinar"
          control={control}
          render={({ field: { value, onChange, name } }) => (
            <NewInput
              type="url"
              name={name}
              value={value}
              label="Webinar"
              placeholder="Link to Tool's Webinar"
              onChange={onChange}
            />
          )}
        />
        {errors.webinar && (
          <p className="text-[#dc2626] text-xs">{errors.webinar.message}</p>
        )}
      </div>
      {itemForm?.map((item, index) => (
        <div key={index} className="relative grid grid-cols-2 w-5/6 gap-5">
          <div>
            <NewInput
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChangeForMutipleItem(e, index, "material")
              }
              value={item.material}
              label={index === 0 ? "Title of tool's supporting material" : ""}
              placeholder="Title of tool's supporting material"
            />
          </div>
          <div>
            <ImageBox
              type="url"
              label="Material"
              placeholder="Type your logo url"
              setState={(e: any) => {
                handleInputChangeForMutipleItem(
                  { target: { value: e } },
                  index,
                  "logo"
                );
              }}
              data={item?.link}
              logo={item?.logo as File}
            />
          </div>
          <div
            onClick={() => handleDeleteRowForMutipleItem(index)}
            className="md:absolute self-center -right-20 cursor-pointer"
          >
            <p className="text-sm text-red-600 flex items-center md:mt-2 gap-2">
              <span>
                <MdDelete />
              </span>
              Remove
            </p>
          </div>
        </div>
      ))}
      <p
        onClick={addRowForMutipleItem}
        className="flex items-center gap-2 text-bluerolodex f-medium my-3 cursor-pointer"
      >
        <span>
          <IoIosAdd />
        </span>
        Add resource
      </p>
    </div>
  );
};

// Utilities Section Component
const UtilitiesSection: React.FC<{
  itemUtil: UtilityItem[];
  handleInputChangeForMutipleItemUtil: (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
    field: string
  ) => void;
  handleDeleteRowForMutipleItemUtil: (index: number) => void;
  addRowForMutipleItemUtils: () => void;
}> = ({
  itemUtil,
  handleInputChangeForMutipleItemUtil,
  handleDeleteRowForMutipleItemUtil,
  addRowForMutipleItemUtils,
}) => {
  return (
    <div className="hidden">
      <label className="text-[#334155] font-medium text-sm inter mt-4">
        Utilities
      </label>
      {itemUtil?.map((item, index) => (
        <div key={index} className="relative grid w-4/5 gap-5">
          <div>
            <NewInput
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleInputChangeForMutipleItemUtil(e, index, "address")
              }
              value={item.address}
              label={index === 0 ? "Address" : ""}
              placeholder="EDAMS Technology"
            />
          </div>
          <div
            onClick={() => handleDeleteRowForMutipleItemUtil(index)}
            className="md:absolute self-center -right-20 cursor-pointer"
          >
            <p className="text-sm text-red-600 flex items-center md:mt-2 gap-2">
              <span>
                <MdDelete />
              </span>
              Remove
            </p>
          </div>
        </div>
      ))}
      <p
        onClick={addRowForMutipleItemUtils}
        className="flex items-center gap-2 text-bluerolodex f-medium my-3 cursor-pointer"
      >
        <span>
          <IoIosAdd />
        </span>
        Add utility
      </p>
    </div>
  );
};

export default GeneralInformationStep;
